import { Sun, Home, Battery, Zap, Car } from 'lucide-react';
import { SiteLiveData } from '../../types';
import type { EVChargerStatus } from '../../api/client';

interface PowerFlowCardProps {
  data: SiteLiveData;
  /** Live EV charger status; undefined/null when the site has no charger. */
  ev?: EVChargerStatus | null;
}

function formatPower(watts: number): string {
  const absWatts = Math.abs(watts);
  if (absWatts >= 1000) {
    return `${(absWatts / 1000).toFixed(1)} kW`;
  }
  return `${Math.round(absWatts)} W`;
}

function formatEnergy(kwh: number): string {
  return `${kwh.toFixed(1)} kWh`;
}

// Animated flow dots component
function FlowDots({
  active,
  reverse = false,
  color,
  pathId,
}: {
  active: boolean;
  reverse?: boolean;
  color: string;
  pathId: string;
}) {
  if (!active) return null;

  // 2 dots spaced evenly across the animation duration
  return (
    <g>
      {[0, 1].map((i) => (
        <circle key={i} r="2" fill={color}>
          <animateMotion
            dur="1.5s"
            repeatCount="indefinite"
            begin={`${i * 0.75}s`}
            keyPoints={reverse ? "1;0" : "0;1"}
            keyTimes="0;1"
            calcMode="linear"
          >
            <mpath href={`#${pathId}`} />
          </animateMotion>
        </circle>
      ))}
    </g>
  );
}

export function PowerFlowCard({ data, ev }: PowerFlowCardProps) {
  const solarPower = data.solar.power_w;
  const loadPower = data.load.power_w;
  const batteryPower = data.battery.power_w;
  const gridPower = data.grid.power_w;
  const batterySoc = data.battery.soc_percent;

  // Determine flow states
  const solarActive = solarPower > 10;
  const curtailment = data.solar.curtailment;
  const batteryCharging = batteryPower < -10;
  const batteryDischarging = batteryPower > 10;
  const gridImporting = gridPower > 10;
  const gridExporting = gridPower < -10;

  // Colors
  const solarColor = '#F59E0B';
  const batteryChargeColor = '#10B981';
  const batteryDischargeColor = '#3B82F6';
  const gridImportColor = '#EF4444';
  const gridExportColor = '#10B981';
  const homeColor = '#8B5CF6';
  const evColor = '#EC4899';

  // EV bubble: drawn whenever the site has a charger reporting; lit while
  // the car is drawing. OCPP chargers meter their own power; a declared
  // (manual) session is an estimate from site load, hence the "~".
  const hasEv = Boolean(ev);
  const evPower = ev ? Number(ev.power_w ?? ev.declared_power_w ?? 0) : 0;
  // Lit on the charger's own "Charging" state: a charger that samples no
  // power measurand reports null/0 W for a minute or two until the
  // gateway derives kW from its energy meter, and the flow should not
  // look idle meanwhile.
  const evCharging = Boolean(ev?.charging);
  const evEstimated = ev?.controllable === false || ev?.power_source === 'derived';

  // Diamond layout positions: Solar top, Grid left, Home right, Battery bottom
  // Larger layout with offset paths to avoid overlapping
  const positions = {
    solar: { x: 100, y: 20 },    // top
    grid: { x: 25, y: 100 },     // left
    home: { x: 175, y: 100 },    // right
    battery: { x: 100, y: 180 }, // bottom
  };
  const center = { x: 100, y: 100 };
  const lineOffset = 6; // Offset to separate parallel lines
  // EV sits to the right of Home, vertically in line with the battery. The
  // viewBox widens to make room so the card itself keeps its size (the
  // diagram scales down a touch instead).
  const evPos = { x: 215, y: positions.battery.y };
  const viewBoxWidth = hasEv ? 236 : 200;

  // Home to EV: out of Home's right side, then down to the EV bubble.
  const homeEvPath = `M ${positions.home.x + 24} ${positions.home.y}
    L ${evPos.x - 8} ${positions.home.y}
    Q ${evPos.x} ${positions.home.y} ${evPos.x} ${positions.home.y + 8}
    L ${evPos.x} ${evPos.y - 20}`;

  // Separated L-shaped paths - each path has its own route to avoid overlapping
  // Solar to Home: down from solar (right side), curve right to home
  const solarHomePath = `M ${positions.solar.x + lineOffset} ${positions.solar.y + 20}
    L ${positions.solar.x + lineOffset} ${center.y - 15}
    Q ${positions.solar.x + lineOffset} ${center.y - lineOffset} ${positions.solar.x + 20} ${center.y - lineOffset}
    L ${positions.home.x - 22} ${center.y - lineOffset}`;

  // Solar to Grid: down from solar (left side), curve left to grid
  const solarGridPath = `M ${positions.solar.x - lineOffset} ${positions.solar.y + 20}
    L ${positions.solar.x - lineOffset} ${center.y - 15}
    Q ${positions.solar.x - lineOffset} ${center.y - lineOffset} ${positions.solar.x - 20} ${center.y - lineOffset}
    L ${positions.grid.x + 22} ${center.y - lineOffset}`;

  // Grid to Battery: right from grid (bottom), curve down to battery (left side)
  const gridBatteryPath = `M ${positions.grid.x + 22} ${center.y + lineOffset}
    L ${center.x - 15} ${center.y + lineOffset}
    Q ${center.x - lineOffset} ${center.y + lineOffset} ${center.x - lineOffset} ${center.y + 20}
    L ${center.x - lineOffset} ${positions.battery.y - 20}`;

  // Home to Battery: left from home (bottom), curve down to battery (right side)
  const homeBatteryPath = `M ${positions.home.x - 22} ${center.y + lineOffset}
    L ${center.x + 15} ${center.y + lineOffset}
    Q ${center.x + lineOffset} ${center.y + lineOffset} ${center.x + lineOffset} ${center.y + 20}
    L ${center.x + lineOffset} ${positions.battery.y - 20}`;

  // Grid to Home: straight horizontal through center
  const gridHomePath = `M ${positions.grid.x + 22} ${center.y} L ${positions.home.x - 22} ${center.y}`;

  // Solar to Battery: straight vertical through center
  const solarBatteryPath = `M ${positions.solar.x} ${positions.solar.y + 20} L ${positions.solar.x} ${positions.battery.y - 20}`;

  return (
    <div className="card p-3 h-full flex flex-col">
      {/* SVG Power Flow Diagram - Diamond Layout with separated L-shaped lines */}
      <div className="flex-1 min-h-0">
        <svg viewBox={`0 0 ${viewBoxWidth} 220`} className="w-full h-full" style={{ maxHeight: '280px' }}>
          {/* Define paths for animation */}
          <defs>
            <path id="solar-grid" d={solarGridPath} fill="none" />
            <path id="solar-home" d={solarHomePath} fill="none" />
            <path id="solar-battery" d={solarBatteryPath} fill="none" />
            <path id="grid-battery" d={gridBatteryPath} fill="none" />
            <path id="home-battery" d={homeBatteryPath} fill="none" />
            <path id="grid-home" d={gridHomePath} fill="none" />
            <path id="home-ev" d={homeEvPath} fill="none" />
          </defs>

          {/* Connection lines - only show when there's flow on that path */}
          {solarActive && gridExporting && (
            <path d={solarGridPath} stroke="#E5E7EB" strokeWidth="0.75" fill="none" className="dark:stroke-gray-600" />
          )}
          {solarActive && loadPower > 10 && (
            <path d={solarHomePath} stroke="#E5E7EB" strokeWidth="0.75" fill="none" className="dark:stroke-gray-600" />
          )}
          {solarActive && batteryCharging && (
            <path d={solarBatteryPath} stroke="#E5E7EB" strokeWidth="0.75" fill="none" className="dark:stroke-gray-600" />
          )}
          {(batteryCharging && gridImporting) || (batteryDischarging && gridExporting) ? (
            <path d={gridBatteryPath} stroke="#E5E7EB" strokeWidth="0.75" fill="none" className="dark:stroke-gray-600" />
          ) : null}
          {batteryDischarging && loadPower > 10 && (
            <path d={homeBatteryPath} stroke="#E5E7EB" strokeWidth="0.75" fill="none" className="dark:stroke-gray-600" />
          )}
          {gridImporting && (
            <path d={gridHomePath} stroke="#E5E7EB" strokeWidth="0.75" fill="none" className="dark:stroke-gray-600" />
          )}
          {hasEv && (
            <path d={homeEvPath} stroke="#E5E7EB" strokeWidth="0.75" fill="none" className="dark:stroke-gray-600" />
          )}

          {/* Solar Icon (top) */}
          <g transform={`translate(${positions.solar.x}, ${positions.solar.y})`}>
            <circle r="20" fill={solarActive ? '#FEF3C7' : '#F3F4F6'} className={solarActive ? '' : 'dark:fill-gray-700'} />
            <Sun x="-12" y="-12" width="24" height="24" stroke={solarActive ? solarColor : '#9CA3AF'} strokeWidth="1.5" fill="none" />
          </g>
          {/* Solar power text */}
          <text x={positions.solar.x} y={positions.solar.y - 28} textAnchor="middle" className="fill-gray-700 dark:fill-gray-300" style={{ fontSize: '11px', fontWeight: 600 }}>
            {formatPower(solarPower)}
          </text>

          {/* Solar curtailment indicator — beside the solar node */}
          {curtailment?.active && (
            <g transform={`translate(${positions.solar.x + 26}, ${positions.solar.y - 6})`}>
              <text
                x={0}
                y={0}
                textAnchor="start"
                fill="#D97706"
                style={{ fontSize: '8px', fontWeight: 700, letterSpacing: '0.04em' }}
              >
                ✂ {curtailment.mode === 'applied' ? 'LIMITED' : 'PLAN LIMIT'}
              </text>
              {curtailment.limit_w != null && (
                <text
                  x={0}
                  y={10}
                  textAnchor="start"
                  fill="#D97706"
                  style={{ fontSize: '8px', fontWeight: 600 }}
                >
                  ≤ {formatPower(curtailment.limit_w)}
                </text>
              )}
            </g>
          )}

          {/* Grid Icon (left) */}
          <g transform={`translate(${positions.grid.x}, ${positions.grid.y})`}>
            <circle r="20" fill={gridImporting ? '#FEE2E2' : gridExporting ? '#D1FAE5' : '#F3F4F6'}
              className={!gridImporting && !gridExporting ? 'dark:fill-gray-700' : ''} />
            <Zap x="-12" y="-12" width="24" height="24"
              stroke={gridImporting ? gridImportColor : gridExporting ? gridExportColor : '#9CA3AF'}
              strokeWidth="1.5" fill="none" />
          </g>
          {/* Grid power text - centered above icon */}
          <text x={positions.grid.x} y={positions.grid.y - 28} textAnchor="middle" className="fill-gray-700 dark:fill-gray-300" style={{ fontSize: '10px', fontWeight: 600 }}>
            {formatPower(Math.abs(gridPower))}
          </text>

          {/* Home Icon (right) */}
          <g transform={`translate(${positions.home.x}, ${positions.home.y})`}>
            <circle r="24" fill="#EDE9FE" className="dark:fill-purple-900/30" />
            <Home x="-14" y="-14" width="28" height="28" stroke={homeColor} strokeWidth="1.5" fill="none" />
          </g>
          {/* Load power text - centered above icon */}
          <text x={positions.home.x} y={positions.home.y - 32} textAnchor="middle" className="fill-gray-700 dark:fill-gray-300" style={{ fontSize: '11px', fontWeight: 600 }}>
            {formatPower(loadPower)}
          </text>

          {/* Battery Icon (bottom) */}
          <g transform={`translate(${positions.battery.x}, ${positions.battery.y})`}>
            <circle r="20" fill={batteryCharging ? '#D1FAE5' : batteryDischarging ? '#DBEAFE' : '#F3F4F6'}
              className={!batteryCharging && !batteryDischarging ? 'dark:fill-gray-700' : ''} />
            <Battery x="-12" y="-12" width="24" height="24"
              stroke={batteryCharging ? batteryChargeColor : batteryDischarging ? batteryDischargeColor : '#9CA3AF'}
              strokeWidth="1.5" fill="none" />
          </g>
          {/* Battery power & SOC text */}
          <text x={positions.battery.x} y={positions.battery.y + 35} textAnchor="middle" className="fill-gray-700 dark:fill-gray-300" style={{ fontSize: '10px', fontWeight: 600 }}>
            {formatPower(Math.abs(batteryPower))} · {batterySoc.toFixed(0)}%
          </text>

          {/* EV Icon (right, level with the battery) */}
          {hasEv && (
            <>
              <g transform={`translate(${evPos.x}, ${evPos.y})`}>
                <circle r="20" fill={evCharging ? '#FCE7F3' : '#F3F4F6'} className={evCharging ? '' : 'dark:fill-gray-700'} />
                <Car x="-12" y="-12" width="24" height="24" stroke={evCharging ? evColor : '#9CA3AF'} strokeWidth="1.5" fill="none" />
              </g>
              <text x={evPos.x} y={evPos.y + 35} textAnchor="middle" className="fill-gray-700 dark:fill-gray-300" style={{ fontSize: '10px', fontWeight: 600 }}>
                {evCharging
                  ? evPower > 10
                    ? `${evEstimated ? '~' : ''}${formatPower(evPower)}`
                    : 'Charging'
                  : 'EV idle'}
              </text>
            </>
          )}

          {/* Animated flow dots */}
          {/* Solar to Home (when solar active and load present) */}
          <FlowDots active={solarActive && loadPower > 10} color={solarColor} pathId="solar-home" />

          {/* Solar to Grid (when exporting) */}
          <FlowDots active={solarActive && gridExporting} color={solarColor} pathId="solar-grid" />

          {/* Solar to Battery (charging from solar) */}
          <FlowDots active={solarActive && batteryCharging} color={solarColor} pathId="solar-battery" />

          {/* Grid to Home (when importing) */}
          <FlowDots active={gridImporting} color={gridImportColor} pathId="grid-home" />

          {/* Grid to Battery (grid charging) */}
          <FlowDots active={batteryCharging && gridImporting} color={batteryChargeColor} pathId="grid-battery" />

          {/* Battery to Home (discharging to load) */}
          <FlowDots active={batteryDischarging && loadPower > 10} reverse color={batteryDischargeColor} pathId="home-battery" />

          {/* Battery to Grid (discharging to export) */}
          <FlowDots active={batteryDischarging && gridExporting} reverse color={batteryDischargeColor} pathId="grid-battery" />

          {/* Home to EV (car drawing) */}
          <FlowDots active={evCharging} color={evColor} pathId="home-ev" />
        </svg>
      </div>

      {/* Daily Energy Summary */}
      <div className="grid grid-cols-2 gap-1 mt-1 text-[10px] border-t border-gray-100 dark:border-gray-700 pt-1">
        <div className="flex justify-between px-1">
          <span className="text-gray-400">Solar</span>
          <span className="text-amber-600 font-medium">{formatEnergy(data.solar.daily_kwh)}</span>
        </div>
        <div className="flex justify-between px-1">
          <span className="text-gray-400">Load</span>
          <span className="text-purple-600 font-medium">{formatEnergy(data.load.daily_kwh)}</span>
        </div>
      </div>
    </div>
  );
}
